import { useState, useRef, useEffect } from 'react';
import { Comment } from '../../types/community';
import { MessageSquare, Users, Loader2 } from 'lucide-react';
import { formatTimeShort } from '../../utils/formatters';
import { motion, AnimatePresence } from 'framer-motion';

interface CommentSectionProps {
  messageId: string;
  comments: Comment[];
  onAddComment: (messageId: string, content: string) => Promise<boolean>;
  // If true, comments are always visible and there is no collapse toggle
  alwaysExpanded?: boolean;
  // If provided, clicking the comment button opens a thread instead of toggling
  onOpenThread?: () => void;
}

export function CommentSection({ messageId, comments, onAddComment, alwaysExpanded = false, onOpenThread }: CommentSectionProps) {
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const commentContainerRef = useRef<HTMLDivElement>(null);
  const effectiveShow = alwaysExpanded ? true : showComments;
  
  // Build a compact, unique list of commenters for avatar stack
  const uniqueCommenters = (() => {
    const profiles = comments.map((c) => c.profiles).filter((p): p is Comment['profiles'] => !!p);
    const map = new Map<string, typeof profiles[number]>();
    profiles.forEach((p) => {
      if (!p.email) return; // safety
      if (!map.has(p.email)) map.set(p.email, p);
    });
    return Array.from(map.values());
  })();
  const avatarProfiles = uniqueCommenters.filter((p) => p.avatar_url).slice(0, 3);
  const extraCount = Math.max(uniqueCommenters.length - avatarProfiles.length, 0);
  
  // Auto-scroll to new comments when they are added
  useEffect(() => {
    if (effectiveShow && commentContainerRef.current) {
      commentContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [effectiveShow, comments.length]);

  const handleSubmit = async () => {
    if (!commentText.trim()) return;
    
    setIsSubmitting(true);
    const success = await onAddComment(messageId, commentText);
    if (success) {
      setCommentText('');
    }
    setIsSubmitting(false);
  };

  return (
    <div className="mt-4 space-y-2 max-w-full overflow-visible">
      <div className="flex flex-wrap items-center justify-between">
        <div className="flex items-center gap-3">
          {alwaysExpanded ? (
            <div
              className="flex items-center justify-center w-9 h-9 bg-[#151D29]/60 backdrop-blur-sm rounded-full text-gray-300 transition-colors relative select-none"
              aria-hidden
            >
              <MessageSquare className="w-4 h-4" />
              {comments.length > 0 && (
                <div className="absolute -top-1 -right-1 bg-[#c9fffc] text-[#151D29] text-xs w-4 h-4 rounded-full flex items-center justify-center font-medium">
                  {comments.length > 9 ? '9+' : comments.length}
                </div>
              )}
            </div>
          ) : (
            <motion.button
              onClick={() => {
                if (onOpenThread) {
                  onOpenThread();
                } else {
                  setShowComments(!showComments);
                }
              }}
              className="flex items-center justify-center w-9 h-9 bg-[#151D29]/60 backdrop-blur-sm rounded-full text-gray-300 hover:text-[#c9fffc] hover:bg-[#c9fffc]/10 transition-colors relative"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <MessageSquare className="w-4 h-4" />
              {comments.length > 0 && (
                <div className="absolute -top-1 -right-1 bg-[#c9fffc] text-[#151D29] text-xs w-4 h-4 rounded-full flex items-center justify-center font-medium">
                  {comments.length > 9 ? '9+' : comments.length}
                </div>
              )}
            </motion.button>
          )}
          {/* Inline commenter avatars next to the comment button */}
          {uniqueCommenters.length > 0 && (
            <div className="flex items-center -space-x-2" title="Participants">
              {avatarProfiles.map((p, idx) => (
                <img
                  key={p.email || idx}
                  src={p.avatar_url as string}
                  alt={p.display_name || p.email}
                  className="w-6 h-6 rounded-full border border-[#0B1220] bg-gray-700 object-cover"
                  onClick={(e) => e.stopPropagation()}
                />
              ))}
              {extraCount > 0 && (
                <div className="w-6 h-6 rounded-full bg-[#151D29] border border-[#0B1220] text-[10px] text-gray-300 flex items-center justify-center select-none">
                  +{extraCount}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      <AnimatePresence>
        {effectiveShow && (
          <motion.div 
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: 'auto', marginTop: 12 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 1, 0.5, 1] }}
            className="overflow-hidden"
          >
            <div className="pl-4 border-l-2 border-[#c9fffc]/20 space-y-3 pb-2" ref={commentContainerRef}>
          {comments
            .slice()
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            .map((comment) => (
              <motion.div 
                key={comment.id} 
                className="bg-[#151D29]/40 backdrop-blur-sm border border-[#c9fffc]/10 rounded-lg p-3 shadow-lg hover:shadow-[#c9fffc]/5 transition-all duration-300"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-6 h-6 rounded-full overflow-hidden bg-gray-700 flex-shrink-0">
                    {comment.profiles?.avatar_url ? (
                      <img
                        src={comment.profiles.avatar_url}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-500">
                        <Users className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                  <span className="text-sm text-[#c9fffc]">
                    {comment.profiles?.display_name || comment.profiles?.email}
                  </span>
                  <span className="text-xs text-gray-500">
                    {formatTimeShort(comment.created_at)}
                  </span>
                </div>
                <p className="text-sm text-gray-300">{comment.content}</p>
              </motion.div>
            ))}
          
          <motion.div 
            className="flex flex-col sm:flex-row gap-2 w-full mt-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <input
              type="text"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Add a comment..."
              className="flex-1 bg-[#151D29]/70 backdrop-blur-sm text-white text-sm border border-[#c9fffc]/10 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#c9fffc] focus:border-[#c9fffc]/30"
            />
            <motion.button
              onClick={handleSubmit}
              disabled={isSubmitting || !commentText.trim()}
              className="bg-[#c9fffc] text-gray-900 rounded-lg px-4 py-2 text-sm font-medium hover:bg-[#a0fcf9] focus:outline-none focus:ring-2 focus:ring-[#c9fffc] disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Comment'
              )}
            </motion.button>
          </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}